<template>
  <h3 class="app-page-title">{{ $t("common7.Buttonhole_calculator") }}</h3>
  <div v-if="loading" class="app-loading-overlay">
    <i class="mdi mdi-loading mdi-spin"></i>
  </div>
  <v-container class="app-pattern-form-container" fluid>
    <v-row justify="center">
      <v-col cols="12" sm="12" md="6" class="app-pattern-form-col">
        <v-form class="app-pattern-form" @submit.prevent="Calculate(true)">
          <tool-description
            :description="$t('buttonhole_calculator.tool_description')"
            :image-path="require('@/assets/tools/buttonhole.jpg')"
            :image-path-mobile="require('@/assets/tools/buttonhole-mobile.jpg')"
            image-alt="Buttonhole example with decorative daisies and bees"
          ></tool-description>

          <div class="app-pattern-form-section">
            <header class="app-pattern-form-header">
              <div>
                {{ $t("common4.Design") }}
              </div>
              <subscription-lock-tooltip
                v-if="!authStore.hasAccess"
                @go-to-subscription="goToSubscription"
              ></subscription-lock-tooltip>
            </header>

            <ribbing-toggle-field
              v-model="formData.patternRibStitchCount"
              :disabled="!authStore.hasAccess"
            >
            </ribbing-toggle-field>
          </div>

          <div class="app-pattern-form-section">
            <header class="app-pattern-form-header">
              <div>
                {{ $t("buttonhole_calculator.Preparation") }}
              </div>
            </header>
            <div class="app-pattern-form-paragraph">
              <span class="preparation-description">
                {{ $t("buttonhole_calculator.Preparation_description") }}
              </span>
              <information-bubble
                class="hint-icon"
                icon="mdi-lightbulb"
                :colors-inverted="true"
                width="medium"
                position="left"
              >
                {{ $t("buttonhole_calculator.Preparation_description_2") }}
              </information-bubble>
            </div>
            <input-field
              class="app-pattern-form-input-field"
              v-model="formData.initialStitches"
              :label="$t('buttonhole_calculator.Initial_stitches')"
              :icon="'mdi-border-left-right'"
              :description="$t('buttonhole_calculator.Initial_stitches_description')"
              :placeholder="$t('buttonhole_calculator.Initial_stitches_placeholder')"
              :small="true"
              :step="1"
            ></input-field>
          </div>

          <div class="app-pattern-form-section">
            <advanced-settings
              :title="$t('common.Advanced_settings')"
              :is-modified="hasCustomAdvancedSettings"
            >
              <div class="app-pattern-form-side-by-side-container">
                <input-field
                  class="app-pattern-form-input-field"
                  v-model="formData.minButtonCount"
                  :label="$t('buttonhole_calculator.Min_buttonholes')"
                  :description="$t('buttonhole_calculator.Min_buttonholes_description')"
                  :placeholder="$t('buttonhole_calculator.Type_in_min_buttonholes')"
                  :small="true"
                  :step="1"
                  :min-value="2"
                  v-model:differs-from-baseline="differsFromBaseline.minButtonCount"
                ></input-field>
                <v-spacer></v-spacer>
                <input-field
                  class="app-pattern-form-input-field"
                  v-model="formData.maxButtonCount"
                  :label="$t('buttonhole_calculator.Max_buttonholes')"
                  :description="$t('buttonhole_calculator.Max_buttonholes_description')"
                  :placeholder="$t('buttonhole_calculator.Type_in_max_buttonholes')"
                  :small="true"
                  :step="1"
                  :min-value="2"
                  v-model:differs-from-baseline="differsFromBaseline.maxButtonCount"
                ></input-field>
              </div>
            </advanced-settings>
          </div>

          <pattern-form-button :label="$t('common6.Calculate')" :disabled="!buttonIsEnabled">
          </pattern-form-button>
        </v-form>
      </v-col>
      <v-col cols="12" sm="12" md="6" v-if="showResults" class="pt-0">
        <div class="app-form-notification app-error" v-if="errorMessage">
          {{ errorMessage }}
        </div>
        <buttonhole-calculator-result
          v-else
          :disabled="formHasChanges"
          :data="resultData"
          @expand-range="handleRangeExpansion"
          @update:selected="handleSelectionUpdate"
          :selected-suggestion="formData.selectedSuggestion"
          :pattern-rib-stitch-count="formData.patternRibStitchCount?.value"
        ></buttonhole-calculator-result>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";

import InputField from "./../../components/general/InputField.vue";
import PatternFormButton from "./../../components/patterns/PatternFormButton.vue";
import ButtonholeCalculatorResult from "./../../components/tools/ButtonholeCalculatorResult.vue";
import RibbingToggleField from "../../components/patterns/RibbingToggleField.vue";
import AdvancedSettings from "../../components/AdvancedSettings";
import ToolDescription from "../../components/tools/ToolDescription.vue";
import { loadCurrentSettings, saveCurrentSettings } from "./../../services/toolSettings";
import { calculateButtonholes } from "./../../services/tool.js";
import InformationBubble from "../../components/InformationBubble.vue";
import { useI18n } from "vue-i18n";
import SubscriptionLockTooltip from "../../components/SubscriptionLockTooltip.vue";
import { useAuthStore } from "../../stores/auth";
import { useRouter } from "vue-router";
import { composeRoute } from "../../lib/navigationUtils";
import { useLangStore } from "../../stores/lang";

const toolType = "buttonholecalculator";
const toolVersion = "1.0.0";

const { t } = useI18n();

const authStore = useAuthStore();
const router = useRouter();
const langStore = useLangStore();
const locale = langStore.getLocale();

const formData = ref({
  initialStitches: null,
  patternRibStitchCount: { value: 2 },
  minButtonCount: 7,
  maxButtonCount: 10,
  selectedSuggestion: null,
});

const showResults = ref(false);
const formHasChanges = ref(false);
const loading = ref(false);

const errorMessage = ref(null);
let resultData = ref(null);

const formIsValid = computed(
  () => {
    return formData.value.initialStitches && formData.value.patternRibStitchCount?.value;
  },
  { deep: true }
);

const hasCustomAdvancedSettings = ref(false);

const differsFromBaseline = ref({
  minButtonCount: false,
  maxButtonCount: false,
});

watch(
  differsFromBaseline,
  (newValue) => {
    hasCustomAdvancedSettings.value = Object.values(newValue).some((value) => value);
  },
  { deep: true }
);

watch(
  formData,
  () => {
    formHasChanges.value = true;
  },
  { deep: true }
);

onMounted(async () => {
  try {
    const loadedForm = await loadCurrentSettings(toolType, toolVersion);
    if (loadedForm != null) {
      formData.value = loadedForm;
      Calculate(false);
    }
  } catch (error) {
    alert(error);
  }
});

const Calculate = async (saveSettings) => {
  if (!formIsValid.value) {
    return;
  }

  try {
    loading.value = true;
    if (formData.value.minButtonCount < 2) {
      errorMessage.value = t("buttonhole_calculator.Min_buttonholes_error");
      return;
    }
    resultData.value = await calculateButtonholes(
      formData.value.initialStitches,
      formData.value.patternRibStitchCount?.value,
      formData.value.minButtonCount,
      formData.value.maxButtonCount
    );

    if (resultData.value.suggestions.length === 0) {
      errorMessage.value = t("buttonhole_calculator.No_suggestions_found");
    } else {
      errorMessage.value = null;
    }

    if (saveSettings) {
      await saveCurrentSettings(toolType, toolVersion, formData.value);
    }
  } catch (error) {
    alert(error);
  } finally {
    formHasChanges.value = false;
    showResults.value = true;
    loading.value = false;
  }
};

const handleRangeExpansion = (min, max) => {
  formData.value.minButtonCount = min;
  formData.value.maxButtonCount = max;
  Calculate(true);
};

const handleSelectionUpdate = async (selectedSuggestion) => {
  formData.value.selectedSuggestion = selectedSuggestion;
  await saveCurrentSettings(toolType, toolVersion, formData.value);
  formHasChanges.value = false;
};

const buttonIsEnabled = computed(() => {
  return formIsValid.value && formHasChanges.value;
});

const goToSubscription = async () => {
  await saveCurrentSettings(toolType, toolVersion, formData.value);
  router.push(composeRoute(locale.value, "subscription", "buttonholecalculator"));
};
</script>

<style lang="scss" scoped>
@import "./../../../scss/tool.scss";
@import "./../../../scss/form.scss";

.preparation-description {
  margin-bottom: 1rem;
  color: var(--knittable-default-text);
}
</style>
